import React, { useState, useEffect } from "react";
import api from "../../../services/httpService";
import "./DashboardSettings.css";

const DashboardSettings = () => {
  const [settings, setSettings] = useState({
    websiteName: "",
    websiteLogo: "",
    websiteLogo2: "",    
    privacyPolicy: "",
    privacyTitle: "",
    privacyImageKey: "",
    color1: "",
    color2: "",
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [selectedLogo2, setSelectedLogo2] = useState(null); 
  const [logo2Preview, setLogo2Preview] = useState(null);   
  const [selectedPrivacyImage, setSelectedPrivacyImage] = useState(null);
  const [privacyImagePreview, setPrivacyImagePreview] = useState(null);
  const [isEditingPolicy, setIsEditingPolicy] = useState(false);
  const [settingsId, setSettingsId] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (settings.color1) document.documentElement.style.setProperty("--theme-color1", settings.color1);
    if (settings.color2) document.documentElement.style.setProperty("--theme-color2", settings.color2);
  }, [settings.color1, settings.color2]);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const res = await api.get("/sentinix/dashboardsettings");
      if (res.data.success && res.data.data) {
        const allData = Array.isArray(res.data.data) ? res.data.data : [res.data.data];
        const latest = allData.sort((a, b) => b.id - a.id)[0];
        if (latest) {
          setSettings({
            websiteName:     latest.websiteName  || "",
            websiteLogo:     latest.websiteLogo  || "",
            websiteLogo2:    latest.websiteLogo2 || "", 
            privacyPolicy:   latest.privacyPolicy || "",
            privacyTitle:    latest.title        || "",
            privacyImageKey: latest.imageKey     || "",
            color1:          latest.color1       || "",
            color2:          latest.color2       || "",
          });
          setSettingsId(latest.id);
        }
      }
    } catch (err) {
      console.error("Failed to load settings:", err);
      setMessage("Error loading settings");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedLogo(file);
    const reader = new FileReader();
    reader.onloadend = () => setLogoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleLogo2Select = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedLogo2(file);
    const reader = new FileReader();
    reader.onloadend = () => setLogo2Preview(reader.result);
    reader.readAsDataURL(file);
  };

  const handlePrivacyImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedPrivacyImage(file);
    const reader = new FileReader();
    reader.onloadend = () => setPrivacyImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    setMessage("");
    try {
      const formData = new FormData();
      formData.append("websiteName",   settings.websiteName   || "");
      formData.append("privacyPolicy", settings.privacyPolicy || "");
      formData.append("title",         settings.privacyTitle  || "");
      formData.append("color1",        settings.color1        || "");
      formData.append("color2",        settings.color2        || "");

      // Logo 1
      if (selectedLogo) {
        formData.append("websiteLogo", selectedLogo);
      } else if (settings.websiteLogo) {
        formData.append("websiteLogo", settings.websiteLogo);
      }

      // Logo 2
      if (selectedLogo2) {
        formData.append("websiteLogo2", selectedLogo2);
      } else if (settings.websiteLogo2) {
        formData.append("websiteLogo2", settings.websiteLogo2);
      }

      // Privacy Image
      if (selectedPrivacyImage) {
        formData.append("imageKey", selectedPrivacyImage);
      } else if (settings.privacyImageKey) {
        formData.append("imageKey", settings.privacyImageKey);
      }

      let res;
      if (settingsId) {
        res = await api.put(`/sentinix/dashboardsettings/${settingsId}`, formData,
          { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        res = await api.post("/sentinix/dashboardsettings", formData,
          { headers: { "Content-Type": "multipart/form-data" } });
      }

      if (res.data.success) {
        setMessage("Settings saved successfully!");
        setSelectedLogo(null); setLogoPreview(null);
        setSelectedLogo2(null); setLogo2Preview(null);
        setSelectedPrivacyImage(null); setPrivacyImagePreview(null);
        setIsEditingPolicy(false);
        fetchSettings();
      } else {
        setMessage("Save failed");
      }
    } catch (err) {
      setMessage(err.response?.data?.message || "Save failed");
    } finally {
      setIsSaving(false);
      setTimeout(() => setMessage(""), 3000);
    }
  };

  if (isLoading) {
    return (
      <div className="dash-settings-container">
        <div className="dash-loading">
          <div className="spinner"></div>
          <p>Loading Settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dash-settings-container">
      <h1 className="dash-settings-title">Settings</h1>

      {message && (
        <div className={`dash-message ${message.includes("success") ? "success" : "error"}`}>
          {message}
        </div>
      )}

      {/* ===== Website Settings Card ===== */}
      <div className="dash-card">
        <h2 className="dash-card-title">Website Settings</h2>

        {/* Website Name */}
        <div className="dash-form-group">
          <label className="dash-label">Website Name</label>
           <input
            type="text"
            className="dash-input"
            value={settings.websiteName}
            onChange={(e) => setSettings({ ...settings, websiteName: e.target.value })}
            placeholder="Enter website name"
          />
        </div>

        {/* TWO LOGOS SIDE BY SIDE */}
        <div className="dash-logos-row">

          {/* Logo 1 */}
          <div className="dash-form-group dash-logo-half">
            <label className="dash-label">Website Logo 1</label>
            <p className="dash-hint">Recommended: 200x200px</p>

            {(logoPreview || settings.websiteLogo) && (
              <div className="dash-logo-preview">
                <img src={logoPreview || settings.websiteLogo} alt="Logo 1" />
              </div>
            )}

            <input type="file" accept="image/*" id="logoInput"
              style={{ display: "none" }} onChange={handleLogoSelect} />
            <button className="dash-btn-change-logo"
              onClick={() => document.getElementById("logoInput").click()}>
              {settings.websiteLogo ? "Change Logo 1" : "Upload Logo 1"}
            </button>

            {selectedLogo
              ? <p className="dash-file-note">✅ {selectedLogo.name}</p>
              : settings.websiteLogo && <p className="dash-file-note" style={{ color: "#888" }}>ℹ️ Existing logo kept</p>
            }
          </div>

          {/* Logo 2 */}
          <div className="dash-form-group dash-logo-half">
            <label className="dash-label">Website Logo 2</label>
            <p className="dash-hint">Recommended: 200x200px</p>

            {(logo2Preview || settings.websiteLogo2) && (
              <div className="dash-logo-preview">
                <img src={logo2Preview || settings.websiteLogo2} alt="Logo 2" />
              </div>
            )}

            <input type="file" accept="image/*" id="logo2Input"
              style={{ display: "none" }} onChange={handleLogo2Select} />
            <button className="dash-btn-change-logo"
              onClick={() => document.getElementById("logo2Input").click()}>
              {settings.websiteLogo2 ? "Change Logo 2" : "Upload Logo 2"}
            </button>

            {selectedLogo2
              ? <p className="dash-file-note">✅ {selectedLogo2.name}</p>
              : settings.websiteLogo2 && <p className="dash-file-note" style={{ color: "#888" }}>ℹ️ Existing logo kept</p>
            }
          </div>

        </div>

        <button className="dash-btn-save" onClick={handleSaveSettings} disabled={isSaving}>
          {isSaving ? "Saving..." : "Save Settings"}
        </button>
      </div>

      {/* ===== Color Theme ===== */}
      <div className="dash-card">
        <h2 className="dash-card-title">Color Theme</h2>
        <p className="dash-hint" style={{ marginBottom: "1.5rem" }}>
          These colors apply across the entire website.
        </p>
        <div className="dash-color-row">
          <div className="dash-color-group">
            <label className="dash-label">Primary Color</label>
            <div className="dash-color-picker-wrap">
              <input type="color" className="dash-color-input"
                value={settings.color1}
                onChange={(e) => setSettings({ ...settings, color1: e.target.value })} />
              <input type="text" className="dash-input dash-color-hex"
                value={settings.color1}
                onChange={(e) => setSettings({ ...settings, color1: e.target.value })}
                placeholder="#f83600" />
            </div>
          </div>
          <div className="dash-color-group">
            <label className="dash-label">Secondary Color</label>
            <div className="dash-color-picker-wrap">
              <input type="color" className="dash-color-input"
                value={settings.color2}
                onChange={(e) => setSettings({ ...settings, color2: e.target.value })} />
              <input type="text" className="dash-input dash-color-hex"
                value={settings.color2}
                onChange={(e) => setSettings({ ...settings, color2: e.target.value })}
                placeholder="#fe8c00" />
            </div>
          </div>
          <div className="dash-color-group">
            <label className="dash-label">Preview</label>
            <div className="dash-color-preview"
              style={{ background: `linear-gradient(135deg, ${settings.color1}, ${settings.color2})` }}>
              <span>Theme Preview</span>
            </div>
          </div>
        </div>
        <button className="dash-btn-save" style={{ marginTop: "1.5rem" }}
          onClick={handleSaveSettings} disabled={isSaving}>
          {isSaving ? "Saving..." : "Save Theme"}
        </button>
      </div>

      {/* ===== Privacy Policy ===== */}
      <div className="dash-card">
        <div className="dash-card-header">
          <h2 className="dash-card-title">Privacy Policy</h2>
          <button className="dash-btn-edit"
            onClick={() => setIsEditingPolicy(!isEditingPolicy)}>
            {isEditingPolicy ? "Cancel" : "Edit"}
          </button>
        </div>

        {isEditingPolicy ? (
          <>
            <div className="dash-form-group">
              <label className="dash-label">Policy Page Title</label>
              <input type="text" className="dash-input"
                value={settings.privacyTitle}
                onChange={(e) => setSettings({ ...settings, privacyTitle: e.target.value })}
                placeholder="e.g. Privacy Policy" />
            </div>
            <div className="dash-form-group">
              <label className="dash-label">Policy Banner Image</label>
              {(privacyImagePreview || settings.privacyImageKey) && (
                <div className="dash-policy-img-preview">
                  <img src={privacyImagePreview || settings.privacyImageKey} alt="Privacy Banner" />
                </div>
              )}
              <input type="file" accept="image/*" id="privacyImageInput"
                style={{ display: "none" }} onChange={handlePrivacyImageSelect} />
              <button className="dash-btn-change-logo"
                onClick={() => document.getElementById("privacyImageInput").click()}>
                {settings.privacyImageKey ? "Change Image" : "Upload Image"}
              </button>
              {selectedPrivacyImage && (
                <p className="dash-file-note">✅ {selectedPrivacyImage.name}</p>
              )}
            </div>
            <div className="dash-form-group">
              <label className="dash-label">Policy Content</label>
              <textarea className="dash-textarea" rows={10}
                value={settings.privacyPolicy}
                onChange={(e) => setSettings({ ...settings, privacyPolicy: e.target.value })}
                placeholder="Enter privacy policy content..." />
            </div>
            <button className="dash-btn-save" onClick={handleSaveSettings} disabled={isSaving}>
              {isSaving ? "Saving..." : "Save Policy"}
            </button>
          </>
        ) : (
          <div className="dash-policy-view">
            {settings.privacyTitle && <h3 className="dash-policy-page-title">{settings.privacyTitle}</h3>}
            {settings.privacyImageKey && (
              <div className="dash-policy-img-preview" style={{ marginBottom: "1rem" }}>
                <img src={settings.privacyImageKey} alt="Privacy Banner" />
              </div>
            )}
            <p className="dash-policy-text">{settings.privacyPolicy || "No privacy policy added yet."}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardSettings;